import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { API, getAuthHeader } from "@/context/AuthContext";
import { useAuth } from "@/context/AuthContext";
import { Plus, Trash2, X, Clock, Sun, Calendar, Users, Edit2, Lock, KeyRound } from "lucide-react";

// ── PIN Modal (protecção de edição para administração) ─────────────────────────
function PinModal({ onSuccess, onCancel }) {
  const [digits, setDigits] = useState(["", "", "", ""]);
  const [error, setError] = useState(false);
  const refs = [useRef(), useRef(), useRef(), useRef()];

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { refs[0].current?.focus(); }, []);

  const handleChange = (i, val) => {
    if (!/^\d?$/.test(val)) return;
    const next = [...digits];
    next[i] = val;
    setDigits(next);
    setError(false);
    if (val && i < 3) refs[i + 1].current?.focus();
  };

  const handleKeyDown = (i, e) => {
    if (e.key === "Backspace" && !digits[i] && i > 0) refs[i - 1].current?.focus();
  };

  const handleConfirm = () => {
    if (digits.join("") === "4750") { onSuccess(); }
    else { setError(true); setDigits(["", "", "", ""]); refs[0].current?.focus(); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-xs p-6 text-center" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-center w-12 h-12 rounded-xl bg-[#0b679e]/10 mx-auto mb-4">
          <KeyRound size={22} className="text-[#0b679e]" />
        </div>
        <h3 className="text-base font-bold text-slate-800 mb-1">Código de Acesso</h3>
        <p className="text-xs text-slate-500 mb-5">Introduza o código para editar este registo</p>
        <div className="flex justify-center gap-3 mb-4">
          {digits.map((d, i) => (
            <input key={i} ref={refs[i]} type="password" inputMode="numeric" maxLength={1} value={d}
              onChange={e => handleChange(i, e.target.value)}
              onKeyDown={e => handleKeyDown(i, e)}
              data-testid={`pin-edit-digit-${i + 1}`}
              className={`w-12 h-12 text-center text-xl font-bold rounded-xl border-2 outline-none transition-colors
                ${error ? "border-red-400 bg-red-50" : "border-slate-200 focus:border-[#0b679e] bg-slate-50"}`} />
          ))}
        </div>
        {error && <p className="text-xs text-red-500 mb-3" data-testid="pin-edit-error">Código incorrecto. Tente novamente.</p>}
        <div className="flex gap-2">
          <button onClick={onCancel} className="flex-1 py-2 rounded-xl border border-slate-200 text-sm text-slate-600 hover:bg-slate-50 transition-colors">Cancelar</button>
          <button onClick={handleConfirm} data-testid="pin-edit-confirm"
            disabled={digits.some(d => d === "")}
            className="flex-1 py-2 rounded-xl bg-[#0b679e] text-white text-sm font-semibold hover:bg-[#084e78] disabled:opacity-40 transition-colors">
            Confirmar
          </button>
        </div>
      </div>
    </div>
  );
}

const TIPO_FERIAS = [
  { key: "ferias",              label: "Férias" },
  { key: "falta_justificada",   label: "Falta Justificada" },
  { key: "falta_injustificada", label: "Falta Injustificada" },
  { key: "outro",               label: "Outro" },
];

function calcTotal(entrada, saida_almoco, regresso_almoco, saida) {
  if (!entrada || !saida) return null;
  const toMin = t => { const [h, m] = (t||"").split(":").map(Number); return h * 60 + m; };
  let total;
  if (saida_almoco && regresso_almoco) {
    total = (toMin(saida_almoco) - toMin(entrada)) + (toMin(saida) - toMin(regresso_almoco));
  } else {
    total = toMin(saida) - toMin(entrada);
  }
  if (total <= 0) return null;
  const h = Math.floor(total / 60), m = total % 60;
  return `${h}h${m > 0 ? String(m).padStart(2, "0") + "m" : ""}`;
}

function calcTotalMin(entrada, saida_almoco, regresso_almoco, saida) {
  if (!entrada || !saida) return 0;
  const toMin = t => { const [h, m] = (t||"").split(":").map(Number); return h * 60 + m; };
  let total;
  if (saida_almoco && regresso_almoco) {
    total = (toMin(saida_almoco) - toMin(entrada)) + (toMin(saida) - toMin(regresso_almoco));
  } else {
    total = toMin(saida) - toMin(entrada);
  }
  return Math.max(0, total);
}

function minsToStr(mins) {
  if (mins <= 0) return "0h";
  const h = Math.floor(mins / 60), m = mins % 60;
  return `${h}h${m > 0 ? String(m).padStart(2, "0") + "m" : ""}`;
}

function anoMes() {
  const n = new Date();
  return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, "0")}`;
}

// ── Ponto Tab ─────────────────────────────────────────────────────────────────
function PontoTab({ utilizadores }) {
  const { user } = useAuth();
  const [registos, setRegistos] = useState([]);
  const [mes, setMes] = useState(anoMes());
  const [filtroUser, setFiltroUser] = useState("");
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editPonto, setEditPonto] = useState(null);
  const [pinPendingRecord, setPinPendingRecord] = useState(null);

  const isAdmin = user?.role === "admin" || user?.role === "administracao";

  const handleEditClick = (r) => {
    if (isAdmin) {
      setPinPendingRecord(r);   // mostrar PIN primeiro
    } else {
      setEditPonto(r);          // colaborador normal — editar directamente (só hoje)
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const load = useCallback(async () => {
    setLoading(true);
    const { data } = await axios.get(`${API}/ponto`, { params: { mes, utilizador_id: filtroUser }, headers: getAuthHeader() });
    setRegistos(data);
    setLoading(false);
  }, [mes, filtroUser]);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (id) => {
    if (!window.confirm("Eliminar este registo?")) return;
    await axios.delete(`${API}/ponto/${id}`, { headers: getAuthHeader() });
    load();
  };

  const today = new Date().toISOString().slice(0, 10);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap gap-2">
          <input type="month" value={mes} onChange={e => setMes(e.target.value)}
            className="px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0b679e]" />
          <select value={filtroUser} onChange={e => setFiltroUser(e.target.value)}
            className="px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0b679e]">
            <option value="">Todos os colaboradores</option>
            {utilizadores.map(u => (
              <option key={u.id} value={u.id}>{u.nome} {u.apelido}</option>
            ))}
          </select>
        </div>
        <button onClick={() => setShowModal(true)} data-testid="registar-ponto-btn"
          className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-white text-sm font-medium"
          style={{ backgroundColor: "#0b679e" }}>
          <Plus size={14} /> Registar Ponto
        </button>
      </div>

      {!loading && registos.length > 0 && (() => {
        const totais = registos.reduce((acc, r) => {
          const key = r.utilizador_id || r.utilizador_nome;
          const mins = calcTotalMin(r.hora_entrada, r.hora_saida_almoco, r.hora_regresso_almoco, r.hora_saida);
          if (!acc[key]) acc[key] = { nome: r.utilizador_nome, mins: 0 };
          acc[key].mins += mins;
          return acc;
        }, {});
        const entries = Object.values(totais).filter(t => t.mins > 0);
        if (!entries.length) return null;
        return (
          <div className="bg-white border border-slate-200 rounded-xl p-4">
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3 flex items-center gap-1.5">
              <Clock size={12} /> Total de horas trabalhadas — {mes}
            </p>
            <div className="flex flex-wrap gap-2">
              {entries.map(({ nome, mins }) => (
                <div key={nome} className="flex items-center gap-2 bg-blue-50 border border-blue-100 rounded-lg px-3 py-2">
                  <span className="text-sm text-slate-700 font-medium">{nome.split(" ")[0]}</span>
                  <span className="text-sm font-bold text-[#0b679e]">{minsToStr(mins)}</span>
                </div>
              ))}
            </div>
          </div>
        );
      })()}

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-200">
              <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Data</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Colaborador</th>
              <th className="text-center px-3 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Entrada</th>
              <th className="text-center px-3 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Saída Almoço</th>
              <th className="text-center px-3 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Regresso</th>
              <th className="text-center px-3 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Saída</th>
              <th className="text-center px-3 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Total</th>
              <th className="px-3 py-3" />
            </tr>
          </thead>
          <tbody>
            {(() => {
              if (loading) return <tr><td colSpan={8} className="text-center py-10 text-slate-400">A carregar...</td></tr>;
              if (registos.length === 0) return <tr><td colSpan={8} className="text-center py-10 text-slate-400">Sem registos para este período</td></tr>;
              return registos.map(r => {
                const isToday = r.data === today;
                const total = calcTotal(r.hora_entrada, r.hora_saida_almoco, r.hora_regresso_almoco, r.hora_saida);
                return (
                  <tr key={r.id} data-testid={`ponto-row-${r.id}`}
                    className={`border-b border-slate-100 hover:bg-slate-50 transition-colors ${isToday ? "bg-blue-50/50" : ""}`}>
                    <td className="px-4 py-3 text-slate-700 font-medium">
                      {new Date(r.data + "T12:00:00").toLocaleDateString("pt-PT", { weekday: "short", day: "2-digit", month: "2-digit" })}
                      {isToday && <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded-full bg-[#0b679e] text-white font-semibold">Hoje</span>}
                    </td>
                    <td className="px-4 py-3 text-slate-700">{r.utilizador_nome}</td>
                    <td className="px-3 py-3 text-center font-mono text-sm text-emerald-700 font-semibold">{r.hora_entrada || "—"}</td>
                    <td className="px-3 py-3 text-center font-mono text-sm text-orange-500">{r.hora_saida_almoco || "—"}</td>
                    <td className="px-3 py-3 text-center font-mono text-sm text-orange-500">{r.hora_regresso_almoco || "—"}</td>
                    <td className="px-3 py-3 text-center font-mono text-sm text-slate-700 font-semibold">{r.hora_saida || "—"}</td>
                    <td className="px-3 py-3 text-center">
                      {total ? (
                        <span className="inline-block px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 text-xs font-semibold">{total}</span>
                      ) : <span className="text-slate-300">—</span>}
                    </td>
                    <td className="px-3 py-3">
                      <div className="flex items-center gap-1">
                        {(isToday || isAdmin) ? (
                          <button onClick={() => handleEditClick(r)} title="Editar registo"
                            className="text-blue-400 hover:text-blue-600 transition-colors p-1">
                            <Edit2 size={13} />
                          </button>
                        ) : (
                          <span className="text-slate-300 p-1" title="Registo bloqueado"><Lock size={12}/></span>
                        )}
                        <button onClick={() => handleDelete(r.id)} className="text-slate-300 hover:text-red-500 transition-colors p-1">
                          <Trash2 size={13} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              });
            })()}
          </tbody>
        </table>
      </div>

      {showModal && <PontoModal utilizadores={utilizadores} onClose={() => setShowModal(false)} onSaved={load} currentUser={user} />}
      {editPonto && <PontoModal utilizadores={utilizadores} onClose={() => setEditPonto(null)} onSaved={load} currentUser={user} editData={editPonto} />}
      {pinPendingRecord && (
        <PinModal
          onSuccess={() => { setEditPonto(pinPendingRecord); setPinPendingRecord(null); }}
          onCancel={() => setPinPendingRecord(null)}
        />
      )}
    </div>
  );
}

function PontoModal({ utilizadores, onClose, onSaved, currentUser, editData }) {
  const [form, setForm] = useState(editData ? {
    utilizador_id: editData.utilizador_id || "",
    utilizador_nome: editData.utilizador_nome || "",
    data: editData.data || "",
    hora_entrada: editData.hora_entrada || "",
    hora_saida_almoco: editData.hora_saida_almoco || "",
    hora_regresso_almoco: editData.hora_regresso_almoco || "",
    hora_saida: editData.hora_saida || "",
  } : {
    utilizador_id: currentUser?.id || "",
    utilizador_nome: `${currentUser?.nome || ""} ${currentUser?.apelido || ""}`.trim(),
    data: new Date().toISOString().slice(0, 10),
    hora_entrada: "",
    hora_saida_almoco: "",
    hora_regresso_almoco: "",
    hora_saida: "",
  });
  const [saving, setSaving] = useState(false);

  const setUser = (uid) => {
    const u = utilizadores.find(u => u.id === uid);
    setForm(f => ({ ...f, utilizador_id: uid, utilizador_nome: u ? `${u.nome} ${u.apelido}` : "" }));
  };

  const submit = async () => {
    if (!form.hora_entrada) { alert("A hora de entrada é obrigatória"); return; }
    setSaving(true);
    try {
      if (editData) {
        await axios.put(`${API}/ponto/${editData.id}`, form, { headers: getAuthHeader() });
      } else {
        await axios.post(`${API}/ponto`, form, { headers: getAuthHeader() });
      }
    } catch(e) {
      alert(e.response?.data?.detail || "Erro ao guardar");
      setSaving(false);
      return;
    }
    setSaving(false);
    onSaved();
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b border-slate-100">
          <h2 className="text-base font-bold text-slate-900">{editData ? "Editar Registo de Ponto" : "Registar Ponto"}</h2>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400"><X size={16} /></button>
        </div>
        <div className="p-5 space-y-3.5">
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Colaborador</label>
            <select value={form.utilizador_id} onChange={e => setUser(e.target.value)}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0b679e]">
              {utilizadores.map(u => (
                <option key={u.id} value={u.id}>{u.nome} {u.apelido}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Data</label>
            <input type="date" value={form.data} onChange={e => setForm(f => ({...f, data: e.target.value}))}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0b679e]" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Entrada <span className="text-red-500">*</span></label>
              <input type="time" value={form.hora_entrada} onChange={e => setForm(f => ({...f, hora_entrada: e.target.value}))}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0b679e]" />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Saída</label>
              <input type="time" value={form.hora_saida} onChange={e => setForm(f => ({...f, hora_saida: e.target.value}))}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0b679e]" />
            </div>
          </div>
          <div className="rounded-xl bg-slate-50 border border-slate-200 p-3 space-y-2.5">
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Almoço</p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Saída p/ almoço</label>
                <input type="time" value={form.hora_saida_almoco} onChange={e => setForm(f => ({...f, hora_saida_almoco: e.target.value}))}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0b679e] bg-white" />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Regresso do almoço</label>
                <input type="time" value={form.hora_regresso_almoco} onChange={e => setForm(f => ({...f, hora_regresso_almoco: e.target.value}))}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0b679e] bg-white" />
              </div>
            </div>
          </div>
          {form.hora_entrada && form.hora_saida && (
            <p className="text-xs text-[#0b679e] font-medium">
              Total: {calcTotal(form.hora_entrada, form.hora_saida_almoco, form.hora_regresso_almoco, form.hora_saida) || "—"}
            </p>
          )}
        </div>
        <div className="flex gap-2 p-5 pt-0">
          <button onClick={onClose} className="flex-1 px-4 py-2.5 border border-slate-300 rounded-lg text-sm text-slate-600 hover:bg-slate-50 transition-colors">Cancelar</button>
          <button onClick={submit} disabled={saving}
            className="flex-1 px-4 py-2.5 rounded-lg text-sm text-white font-medium transition-colors disabled:opacity-50"
            style={{ backgroundColor: "#0b679e" }}>
            {saving ? "A guardar..." : "Guardar"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Férias Tab ────────────────────────────────────────────────────────────────
function FeriasTab({ utilizadores }) {
  const { user } = useAuth();
  const [registos, setRegistos] = useState([]);
  const [ano, setAno] = useState(String(new Date().getFullYear()));
  const [filtroUser, setFiltroUser] = useState("");
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const { data } = await axios.get(`${API}/ferias`, { params: { ano, utilizador_id: filtroUser }, headers: getAuthHeader() });
    setRegistos(data);
    setLoading(false);
  }, [ano, filtroUser]);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (id) => {
    if (!window.confirm("Eliminar este registo?")) return;
    await axios.delete(`${API}/ferias/${id}`, { headers: getAuthHeader() });
    load();
  };

  // Total days per user
  const totaisPorUser = registos.reduce((acc, r) => {
    acc[r.utilizador_nome] = (acc[r.utilizador_nome] || 0) + (r.num_dias || 0);
    return acc;
  }, {});

  const TIPO_COLORS = {
    ferias: "bg-blue-100 text-blue-700",
    falta_justificada: "bg-yellow-100 text-yellow-700",
    falta_injustificada: "bg-red-100 text-red-700",
    outro: "bg-slate-100 text-slate-700",
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap gap-2">
          <select value={ano} onChange={e => setAno(e.target.value)}
            className="px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0b679e]">
            {[2026,2025,2024,2023].map(y => <option key={y}>{y}</option>)}
          </select>
          <select value={filtroUser} onChange={e => setFiltroUser(e.target.value)}
            className="px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0b679e]">
            <option value="">Todos os colaboradores</option>
            {utilizadores.map(u => (
              <option key={u.id} value={u.id}>{u.nome} {u.apelido}</option>
            ))}
          </select>
        </div>
        <button onClick={() => setShowModal(true)} data-testid="registar-ferias-btn"
          className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-white text-sm font-medium"
          style={{ backgroundColor: "#0b679e" }}>
          <Plus size={14} /> Registar Férias
        </button>
      </div>

      {/* Totals per user */}
      {Object.keys(totaisPorUser).length > 0 && (
        <div className="flex flex-wrap gap-2">
          {Object.entries(totaisPorUser).map(([nome, dias]) => (
            <div key={nome} className="bg-white border border-slate-200 rounded-lg px-3 py-2 flex items-center gap-2">
              <Users size={12} className="text-[#0b679e]" />
              <span className="text-xs text-slate-600">{nome.split(" ")[0]}</span>
              <span className="text-xs font-bold text-[#0b679e]">{dias}d</span>
            </div>
          ))}
        </div>
      )}

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-200">
              <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Colaborador</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Período</th>
              <th className="text-center px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Dias</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Tipo</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Observações</th>
              <th className="px-3 py-3" />
            </tr>
          </thead>
          <tbody>
            {(() => {
              if (loading) return <tr><td colSpan={6} className="text-center py-10 text-slate-400">A carregar...</td></tr>;
              if (registos.length === 0) return <tr><td colSpan={6} className="text-center py-10 text-slate-400">Sem registos para {ano}</td></tr>;
              return registos.map(r => (
                <tr key={r.id} data-testid={`ferias-row-${r.id}`}
                  className="border-b border-slate-100 hover:bg-slate-50 transition-colors">
                  <td className="px-4 py-3 font-medium text-slate-900">{r.utilizador_nome}</td>
                  <td className="px-4 py-3 text-slate-600">
                    {new Date(r.data_inicio + "T12:00:00").toLocaleDateString("pt-PT", { day:"2-digit", month:"short" })}
                    {" → "}
                    {new Date(r.data_fim + "T12:00:00").toLocaleDateString("pt-PT", { day:"2-digit", month:"short", year:"numeric" })}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span className="inline-block px-2 py-0.5 rounded-full bg-[#0b679e]/10 text-[#0b679e] text-xs font-bold">{r.num_dias}d</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${TIPO_COLORS[r.tipo] || "bg-slate-100 text-slate-600"}`}>
                      {TIPO_FERIAS.find(t => t.key === r.tipo)?.label || r.tipo}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-slate-500 text-xs">{r.observacoes || "—"}</td>
                  <td className="px-3 py-3">
                    <button onClick={() => handleDelete(r.id)} className="text-slate-300 hover:text-red-500 transition-colors p-1">
                      <Trash2 size={13} />
                    </button>
                  </td>
                </tr>
              ));
            })()}
          </tbody>
        </table>
      </div>

      {showModal && <FeriasModal utilizadores={utilizadores} onClose={() => setShowModal(false)} onSaved={load} currentUser={user} />}
    </div>
  );
}

function FeriasModal({ utilizadores, onClose, onSaved, currentUser }) {
  const [form, setForm] = useState({
    utilizador_id: currentUser?.id || "",
    utilizador_nome: `${currentUser?.nome || ""} ${currentUser?.apelido || ""}`.trim(),
    data_inicio: new Date().toISOString().slice(0, 10),
    data_fim: new Date().toISOString().slice(0, 10),
    tipo: "ferias",
    observacoes: "",
  });
  const [saving, setSaving] = useState(false);

  const setUser = (uid) => {
    const u = utilizadores.find(u => u.id === uid);
    setForm(f => ({ ...f, utilizador_id: uid, utilizador_nome: u ? `${u.nome} ${u.apelido}` : "" }));
  };

  const numDias = form.data_inicio && form.data_fim
    ? Math.max(0, Math.ceil((new Date(form.data_fim) - new Date(form.data_inicio)) / 86400000) + 1)
    : 0;

  const submit = async () => {
    if (!form.data_inicio || !form.data_fim) { alert("Datas obrigatórias"); return; }
    if (form.data_fim < form.data_inicio) { alert("Data de fim deve ser após data de início"); return; }
    setSaving(true);
    await axios.post(`${API}/ferias`, form, { headers: getAuthHeader() });
    setSaving(false);
    onSaved();
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b border-slate-100">
          <h2 className="text-base font-bold text-slate-900">Registar Férias / Ausência</h2>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400"><X size={16} /></button>
        </div>
        <div className="p-5 space-y-3.5">
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Colaborador</label>
            <select value={form.utilizador_id} onChange={e => setUser(e.target.value)}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0b679e]">
              {utilizadores.map(u => (
                <option key={u.id} value={u.id}>{u.nome} {u.apelido}</option>
              ))}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Data início</label>
              <input type="date" value={form.data_inicio} onChange={e => setForm(f => ({...f, data_inicio: e.target.value}))}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0b679e]" />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Data fim</label>
              <input type="date" value={form.data_fim} onChange={e => setForm(f => ({...f, data_fim: e.target.value}))}
                min={form.data_inicio}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0b679e]" />
            </div>
          </div>
          {numDias > 0 && (
            <p className="text-xs text-[#0b679e] font-medium">{numDias} dia{numDias !== 1 ? "s" : ""}</p>
          )}
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Tipo</label>
            <select value={form.tipo} onChange={e => setForm(f => ({...f, tipo: e.target.value}))}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0b679e]">
              {TIPO_FERIAS.map(t => <option key={t.key} value={t.key}>{t.label}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Observações</label>
            <input value={form.observacoes} onChange={e => setForm(f => ({...f, observacoes: e.target.value}))}
              placeholder="Opcional..."
              className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0b679e]" />
          </div>
        </div>
        <div className="flex gap-2 p-5 pt-0">
          <button onClick={onClose} className="flex-1 px-4 py-2.5 border border-slate-300 rounded-lg text-sm text-slate-600 hover:bg-slate-50 transition-colors">Cancelar</button>
          <button onClick={submit} disabled={saving}
            className="flex-1 px-4 py-2.5 rounded-lg text-sm text-white font-medium transition-colors disabled:opacity-50"
            style={{ backgroundColor: "#0b679e" }}>
            {saving ? "A guardar..." : "Guardar"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function PontoFerias() {
  const [activeTab, setActiveTab] = useState("ponto");
  const [utilizadores, setUtilizadores] = useState([]);

  // Carga única de utilizadores (API/axios/getAuthHeader são referências de módulo estáveis)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    axios.get(`${API}/utilizadores`, { headers: getAuthHeader() })
      .then(r => setUtilizadores(r.data || []))
      .catch(() => {});
  }, []);

  const TABS = [
    { key: "ponto",  label: "Registo de Ponto", icon: Clock },
    { key: "ferias", label: "Férias & Ausências", icon: Sun },
  ];

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Ponto & Férias</h1>
        <p className="text-sm text-slate-500 mt-0.5">Gestão de horários e ausências da equipa</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-slate-100 p-1 rounded-xl w-fit">
        {TABS.map(({ key, label, icon: Icon }) => (
          <button key={key} onClick={() => setActiveTab(key)}
            data-testid={`tab-${key}`}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              activeTab === key ? "bg-white text-[#0b679e] shadow-sm" : "text-slate-500 hover:text-slate-700"
            }`}>
            <Icon size={14} /> {label}
          </button>
        ))}
      </div>

      {activeTab === "ponto"  && <PontoTab  utilizadores={utilizadores} />}
      {activeTab === "ferias" && <FeriasTab utilizadores={utilizadores} />}
    </div>
  );
}
